import React from "react";
import { motion } from "framer-motion";
import { Wifi, Droplets, Camera, Dumbbell, Zap, Moon } from "lucide-react";

const facilities = [
  {
    icon: Dumbbell,
    title: "Premium Equipment",
    desc: "Top-tier machines, free weights, and functional rigs—maintained for peak performance.",
    img: "https://images.unsplash.com/photo-1770513649465-2c60c8039806?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxneW0lMjBkYXJrJTIwbW9kZXJufGVufDB8fHx8MTc3NDc4NjM5N3ww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    icon: Camera,
    title: "Creator Zones",
    desc: "Dedicated filming areas with pro lighting for reels and transformations.",
    img: "https://images.unsplash.com/photo-1693214674451-2111f7690877?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxneW0lMjBkYXJrJTIwbW9kZXJufGVufDB8fHx8MTc3NDc4NjM5N3ww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-1",
  },
  {
    icon: Moon,
    title: "Late-Night Access",
    desc: "Open 5AM–12AM, 7 days a week. Train when the world slows down.",
    span: "md:col-span-1",
  },
  {
    icon: Droplets,
    title: "Recovery Suite",
    desc: "Cold plunge, sauna, and foam rolling stations for optimal recovery.",
    span: "md:col-span-1",
  },
  {
    icon: Wifi,
    title: "Smart Training",
    desc: "Digital tracking, form analysis, and personalized workout logging.",
    span: "md:col-span-1",
  },
  {
    icon: Zap,
    title: "High-Energy Atmosphere",
    desc: "Curated playlists, LED ambience, and mirror-heavy layouts built to motivate.",
    span: "md:col-span-1",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1 },
  }),
};

export default function FacilitiesSection() {
  return (
    <section id="facilities" data-testid="facilities-section" className="py-20 sm:py-24 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={0}
        >
          <span className="text-[#F59E0B] text-sm font-semibold tracking-[0.3em] uppercase">Our Space</span>
        </motion.div>
        <motion.h2
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={1}
          className="font-['Bebas_Neue'] text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-4 mb-16"
        >
          Built to <span className="text-[#F59E0B]">Push Limits</span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {facilities.map((f, i) => (
            <motion.div
              key={f.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={i + 2}
              data-testid={`facility-${f.title.toLowerCase().replace(/\s/g, '-')}`}
              className={`group relative overflow-hidden border border-[#262626] bg-[#121212] ${f.span} ${
                f.img ? "min-h-[280px] md:min-h-[360px]" : "p-8 flex flex-col justify-between min-h-[200px]"
              }`}
            >
              {f.img ? (
                <>
                  <img
                    src={f.img}
                    alt={f.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="relative z-10 flex flex-col justify-end h-full p-8">
                    <f.icon className="w-6 h-6 text-[#F59E0B] mb-3" />
                    <h3 className="font-['Bebas_Neue'] text-2xl tracking-wide text-white mb-2">{f.title}</h3>
                    <p className="text-sm text-[#A3A3A3] max-w-sm">{f.desc}</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <f.icon className="w-6 h-6 text-[#F59E0B] mb-4" />
                    <h3 className="font-['Bebas_Neue'] text-2xl tracking-wide text-white mb-3">{f.title}</h3>
                    <p className="text-sm text-[#A3A3A3]">{f.desc}</p>
                  </div>
                  <div className="mt-4 h-px bg-gradient-to-r from-[#F59E0B]/40 to-transparent" />
                </>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
